import {
    ReportItemSummaryType,
    ReportItemType,
    ReportSensorMeterType,
    ReportSensorType,
} from "types/Reports";

export type ReportSensorRegistryEntry = {
    colorHex: string;
    colorClassName: string;
    backgroundColorClassName: string;

    unit: string;
    bullet: string;
    tabTotal: string;
    edgeSubject: string;
    percentSubject: string;
    summaryName: string;

    pdfFilterDetailTitle: string;
    pdfFilterDetailTotal: string;
    filterEmptySubject: string;
    filterMax: number;

    pdfIndividualTitle: string;

    edgeCountSingular: string;
    edgeCountPlural: string;
    edgeUsedFragment: string;
    averageSubject: string;
    individualUsageWord: string;

    // Device-type string used to match `sensor.sensorType` from gateway data.
    // Only present for sensor/meter types that have a physical device type string;
    // 'room', 'energy', and 'water' intentionally omit this.
    deviceSensorType?: string;

    // PDF overview section info, only applicable to summary types
    // ('room' | 'energy' | 'water' | 'waterLeakage').
    pdfOverview?: {
        title: string;
        overallCardTitle: string;
        individualCardTitle: string;
        individualTableHeaders: string[];
    };
};

export const REPORT_SENSOR_REGISTRY: Record<ReportItemType, ReportSensorRegistryEntry> = {
    room: {
        colorHex: '#F2994A',
        colorClassName: 'report-page-room-color',
        backgroundColorClassName: 'report-page-room-background-color',
        unit: 'hours',
        bullet: 'Room Occupancy (hours)',
        tabTotal: 'Total Room Occupancy',
        edgeSubject: 'room occupancy',
        percentSubject: 'Room occupancy',
        summaryName: 'Room',
        pdfFilterDetailTitle: 'Rooms',
        pdfFilterDetailTotal: 'Total Rooms',
        filterEmptySubject: 'rooms',
        filterMax: 64,
        pdfIndividualTitle: 'Individual Room Insights',
        edgeCountSingular: 'room',
        edgeCountPlural: 'rooms',
        edgeUsedFragment: '',
        averageSubject: 'room occupancy was recorded',
        individualUsageWord: 'room occupancy',
        pdfOverview: {
            title: 'Room Occupancy',
            overallCardTitle: 'Overall Room Occupancy',
            individualCardTitle: 'Individual Room Occupancy',
            individualTableHeaders: ['Rooms', 'Room Occupancy', 'Busiest Day', 'Daily Average'],
        },
    },
    energy: {
        colorHex: '#5EBFC6',
        colorClassName: 'report-page-energy-color',
        backgroundColorClassName: 'report-page-energy-background-color',
        unit: 'kWh',
        bullet: 'Energy Consumption (kWh)',
        tabTotal: 'Total Energy Consumption',
        edgeSubject: 'energy usage',
        percentSubject: 'Energy usage',
        summaryName: 'Energy Meter',
        pdfFilterDetailTitle: 'Energy Meters',
        pdfFilterDetailTotal: 'Total Energy Meters',
        filterEmptySubject: 'energy meters',
        filterMax: 32,
        pdfIndividualTitle: 'Individual Energy Meter Insights',
        edgeCountSingular: 'energy meter',
        edgeCountPlural: 'energy meters',
        edgeUsedFragment: 'used',
        averageSubject: 'energy was used',
        individualUsageWord: 'energy',
        pdfOverview: {
            title: 'Energy Consumption',
            overallCardTitle: 'Overall Energy Consumption',
            individualCardTitle: 'Individual Energy Meter Consumption',
            individualTableHeaders: ['Meters', 'Energy Consumption', 'Highest Consumed Day', 'Daily Average'],
        },
    },
    water: {
        colorHex: '#82C771',
        colorClassName: 'report-page-water-color',
        backgroundColorClassName: 'report-page-water-background-color',
        unit: 'm³',
        bullet: 'Water Consumption (m³)',
        tabTotal: 'Total Water Consumption',
        edgeSubject: 'water usage',
        percentSubject: 'Water usage',
        summaryName: 'Water Flow Meter',
        pdfFilterDetailTitle: 'Water Flow Meters',
        pdfFilterDetailTotal: 'Total Water Flow Meters',
        filterEmptySubject: 'water flow meters',
        filterMax: 32,
        pdfIndividualTitle: 'Individual Water Flow Meter Insights',
        edgeCountSingular: 'water flow meter',
        edgeCountPlural: 'water flow meters',
        edgeUsedFragment: 'of water used',
        averageSubject: 'water was used',
        individualUsageWord: 'water',
        pdfOverview: {
            title: 'Water Consumption',
            overallCardTitle: 'Overall Water Consumption',
            individualCardTitle: 'Individual Water Flow Meter Consumption',
            individualTableHeaders: ['Meters', 'Water Consumption', 'Highest Consumed Day', 'Daily Average'],
        },
    },
    waterLeakage: {
        colorHex: '#66C2A5',
        colorClassName: 'report-page-water-leakage-color',
        backgroundColorClassName: 'report-page-water-leakage-background-color',
        unit: '',
        bullet: 'Leakage Detection Count',
        tabTotal: 'Total Leakage Detection Count',
        edgeSubject: 'leakage detection',
        percentSubject: '',
        summaryName: 'Water Leakage Detector',
        pdfFilterDetailTitle: 'Water Leakage Detector Sensors',
        pdfFilterDetailTotal: 'Total Water Leakage Detector Sensors',
        filterEmptySubject: 'water leakage detector sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual Water Leakage Detection Sensor Insights',
        edgeCountSingular: 'sensor',
        edgeCountPlural: 'sensors',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: 'leakage detection',
        deviceSensorType: 'Water Detection Sensor',
        pdfOverview: {
            title: 'Water Leakage Detection',
            overallCardTitle: 'Overall Water Leakage Detection',
            individualCardTitle: 'Individual Leakage Detection Sensor',
            individualTableHeaders: ['Sensors', 'Detection Count', 'Highest Leakage Detection Day'],
        },
    },
    temperature: {
        colorHex: '#577DCA',
        colorClassName: 'report-page-temperature-color',
        backgroundColorClassName: 'report-page-temperature-background-color',
        unit: '°C',
        bullet: 'Temperature (°C)',
        tabTotal: 'Monthly Average Temperature',
        edgeSubject: 'temperature',
        percentSubject: 'Temperature',
        summaryName: 'Temperature Sensor',
        pdfFilterDetailTitle: 'Temperature Sensors',
        pdfFilterDetailTotal: 'Temperature Sensors',
        filterEmptySubject: 'temperature sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual Temperature Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'Temperature Sensor',
    },
    humidity: {
        colorHex: '#36AF5E',
        colorClassName: 'report-page-humidity-color',
        backgroundColorClassName: 'report-page-humidity-background-color',
        unit: '%',
        bullet: 'Humidity (%)',
        tabTotal: 'Monthly Average Humidity',
        edgeSubject: 'humidity',
        percentSubject: 'Humidity',
        summaryName: 'Humidity Sensor',
        pdfFilterDetailTitle: 'Humidity Sensors',
        pdfFilterDetailTotal: 'Humidity Sensors',
        filterEmptySubject: 'humidity sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual Humidity Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'Humidity Sensor',
    },
    co2: {
        colorHex: '#7B98B5',
        colorClassName: 'report-page-co2-color',
        backgroundColorClassName: 'report-page-co2-background-color',
        unit: 'ppm',
        bullet: 'CO2 (ppm)',
        tabTotal: 'Monthly Average CO2',
        edgeSubject: 'CO2',
        percentSubject: 'CO2',
        summaryName: 'CO2 Sensor',
        pdfFilterDetailTitle: 'CO2 Sensors',
        pdfFilterDetailTotal: 'CO2 Sensors',
        filterEmptySubject: 'CO2 sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual CO2 Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'CO2 Sensor',
    },
    pressure: {
        colorHex: '#80CBC4',
        colorClassName: 'report-page-pressure-color',
        backgroundColorClassName: 'report-page-pressure-background-color',
        unit: 'hPa',
        bullet: 'Pressure (hPa)',
        tabTotal: 'Monthly Average Pressure',
        edgeSubject: 'pressure',
        percentSubject: 'Pressure',
        summaryName: 'Pressure Sensor',
        pdfFilterDetailTitle: 'Pressure Sensors',
        pdfFilterDetailTotal: 'Pressure Sensors',
        filterEmptySubject: 'pressure sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual Pressure Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'Pressure Sensor',
    },
    tvoc: {
        colorHex: '#957DDE',
        colorClassName: 'report-page-tvoc-color',
        backgroundColorClassName: 'report-page-tvoc-background-color',
        unit: 'ppb',
        bullet: 'TVOC (ppb)',
        tabTotal: 'Monthly Average TVOC',
        edgeSubject: 'TVOC',
        percentSubject: 'TVOC',
        summaryName: 'TVOC Sensor',
        pdfFilterDetailTitle: 'TVOC Sensors',
        pdfFilterDetailTotal: 'TVOC Sensors',
        filterEmptySubject: 'TVOC sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual TVOC Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'VOC Sensor',
    },
    pm1: {
        colorHex: '#76B8D5',
        colorClassName: 'report-page-pm1-color',
        backgroundColorClassName: 'report-page-pm1-background-color',
        unit: 'µg/m³',
        bullet: 'PM1.0 (µg/m³)',
        tabTotal: 'Monthly Average PM1.0',
        edgeSubject: 'PM1.0',
        percentSubject: 'PM1.0',
        summaryName: 'PM1.0 Sensor',
        pdfFilterDetailTitle: 'PM1.0 Sensors',
        pdfFilterDetailTotal: 'PM1.0 Sensors',
        filterEmptySubject: 'PM1.0 sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual PM1.0 Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'PM1.0 Sensor',
    },
    'pm2.5': {
        colorHex: '#6BC48C',
        colorClassName: 'report-page-pm2-5-color',
        backgroundColorClassName: 'report-page-pm2-5-background-color',
        unit: 'µg/m³',
        bullet: 'PM2.5 (µg/m³)',
        tabTotal: 'Monthly Average PM2.5',
        edgeSubject: 'PM2.5',
        percentSubject: 'PM2.5',
        summaryName: 'PM2.5 Sensor',
        pdfFilterDetailTitle: 'PM2.5 Sensors',
        pdfFilterDetailTotal: 'PM2.5 Sensors',
        filterEmptySubject: 'PM2.5 sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual PM2.5 Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'PM2.5 Sensor',
    },
    pm10: {
        colorHex: '#CCA96D',
        colorClassName: 'report-page-pm10-color',
        backgroundColorClassName: 'report-page-pm10-background-color',
        unit: 'µg/m³',
        bullet: 'PM10 (µg/m³)',
        tabTotal: 'Monthly Average PM10',
        edgeSubject: 'PM10',
        percentSubject: 'PM10',
        summaryName: 'PM10 Sensor',
        pdfFilterDetailTitle: 'PM10 Sensors',
        pdfFilterDetailTotal: 'PM10 Sensors',
        filterEmptySubject: 'PM10 sensors',
        filterMax: 32,
        pdfIndividualTitle: 'Individual PM10 Sensor Insights',
        edgeCountSingular: '',
        edgeCountPlural: '',
        edgeUsedFragment: '',
        averageSubject: 'was used',
        individualUsageWord: '',
        deviceSensorType: 'PM10 Sensor',
    },
};

// Ordered item lists, kept as literal arrays so consumers that rely on
// iteration order (e.g. tab rendering, PDF sections) remain stable.
export const REPORT_SUMMARY_ITEMS: ReportItemSummaryType[] = ['room', 'energy', 'water', 'waterLeakage'];
export const REPORT_ITEM_LIST: ReportItemType[] = [
    'room', 'energy', 'water', 'waterLeakage',
    'temperature', 'humidity', 'co2', 'pressure',
    'tvoc', 'pm1', 'pm2.5', 'pm10',
];
export const REPORT_METER_SENSOR_LIST: ReportSensorMeterType[] = [
    'energy', 'water', 'waterLeakage',
    'temperature', 'humidity', 'co2', 'pressure',
    'tvoc', 'pm1', 'pm2.5', 'pm10',
];
export const REPORT_SENSOR_LIST: ReportSensorType[] = [
    'temperature', 'humidity', 'co2', 'pressure',
    'tvoc', 'pm1', 'pm2.5', 'pm10',
];

export const DEFAULT_ACCURACY: string = "4";
export const LESS_ACCURACY: string = "2";
export const PERCENTAGE_ACCURACY: string = "1";
export const COUNT_ACCURACY: string = "0";
export const MAX_LENGTH_PDF_ROOM_SUMMARY_CARD_VALUE_SIZE = 14;
export const MAX_LENGTH_PDF_SUMMARY_CARD_VALUE_SIZE = 23;
export const MAX_LENGTH_PDF_INDIVIDUAL_TABLE_VALUE_SIZE = 16;
export const MAX_LENGTH_PDF_INDIVIDUAL_TABLE_AVERAGE_VALUE_SIZE = 11;

export const HUB_VIRTUAL_LDSU_OBJ = "45057";
export const ZONE_VIRTUAL_LDSU_OBJ = "45058";
export const ROOM_VIRTUAL_LDSU_OBJ = "45058";
export const GROUP_VIRTUAL_LDSU_OBJ = "45057";

export const ZONE_TAG_PREFIX = "zone/";
export const ROOM_TAG_PREFIX = "room/";
export const GROUP_TAG_PREFIX = "group/";
